/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState } from 'react';

import { jsx } from '@compiled/react';

import { ButtonGroup } from '@atlaskit/button';
import Button, { IconButton } from '@atlaskit/button/new';
import { Checkbox } from '@atlaskit/checkbox';
import Form, { Field, Fieldset } from '@atlaskit/form';
import CrossIcon from '@atlaskit/icon/core/cross';
import Modal, { ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Grid, Text, xcss } from '@atlaskit/primitives';
import TextField from '@atlaskit/textfield';

import { MODAL_ACTION } from '../../../utils/analytics';

import { type SearchFeedbackModalProps } from './types';

const gridStyles = xcss({
	width: '100%',
});

const closeContainerStyles = xcss({
	gridArea: 'close',
});

const titleContainerStyles = xcss({
	gridArea: 'title',
});

const options = [
	'It was faster',
	'Results came from trusted sources',
	'AI Answer above search results was better',
	'Result I needed was higher',
	'Results were more personalized',
	'I prefer the visual organization',
	'URL ranking',
	'Answer triggering',
	'Answer quality',
	'3P results',
	'Caption quality',
];

const textFieldStyles = xcss({
	marginTop: 'space.250',
});

const fieldsetStyles = xcss({
	marginTop: 'space.negative.050',
});

const customFooterStyles = xcss({
	padding: 'space.300',
	borderTopColor: 'color.border',
	borderTopStyle: 'solid',
	borderTopWidth: 'border.width',
	marginTop: 'space.300',
});

const customBodyStyles = xcss({
	marginTop: 'space.negative.150',
	flexGrow: 1,
	flex: '1 1 auto',
	paddingTop: 'space.025',
	paddingBottom: 'space.025',
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
});

/**
 * data is submitted in the form:
 * {'It was faster': undefined, 'Result I needed was higher': true}
 * so this will find keys with a true value and add them to the list
 */
const getRatingReasons = (options: Record<string, any>): string[] => {
	const selectedOptions: string[] = [];
	for (const [key, value] of Object.entries(options)) {
		if (!!value) {
			selectedOptions.push(key);
		}
	}
	return selectedOptions;
};

export const SearchFeedbackModal = ({ onSubmitAndClose }: SearchFeedbackModalProps) => {
	const [shouldAttachScreenshot, setShouldAttachScreenshot] = useState<boolean>(true);
	const handleSubmit = (data: any) => {
		const { feedback, ...rest } = data;
		const ratingReasons = getRatingReasons(rest);
		onSubmitAndClose({
			feedback,
			ratingReasons,
			action: MODAL_ACTION.SUBMITTED,
			shouldAttachScreenshot,
		});
	};
	const handleScreenshotCheckbox = () => {
		setShouldAttachScreenshot((state) => !state);
	};

	return (
		<div data-testid="search-feedback-modal">
			<ModalTransition>
				<Modal
					width="large"
					onClose={() =>
						onSubmitAndClose({
							action: MODAL_ACTION.CLOSED,
							ratingReasons: [],
							shouldAttachScreenshot: false,
						})
					}
				>
					{fg('platform-design_system_team-form_conversion') ? (
						<Form onSubmit={handleSubmit}>
							<ModalHeader>
								<Grid gap="space.200" templateAreas={['title close']} xcss={gridStyles}>
									<Flex xcss={closeContainerStyles} justifyContent="end">
										<IconButton
											appearance="subtle"
											icon={CrossIcon}
											label="Close Modal"
											onClick={() =>
												onSubmitAndClose({
													action: MODAL_ACTION.CLOSED,
													ratingReasons: [],
													shouldAttachScreenshot: false,
												})
											}
										/>
									</Flex>
									<Flex xcss={titleContainerStyles} justifyContent="start">
										{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
										<ModalTitle>You're in the raffle! Tell us more</ModalTitle>
									</Flex>
								</Grid>
							</ModalHeader>
							<Box xcss={customBodyStyles}>
								<Flex direction="column" gap="space.200">
									{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
									<Text>
										Every time you rate your search results you’ll be in a fortnightly raffle to win
										a kudos.
									</Text>
									{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
									<Text weight="semibold">Why do you prefer these search results?</Text>
								</Flex>
								<Box xcss={fieldsetStyles}>
									<Fieldset>
										{options.map((option, id) => (
											<Field key={`${id}-${option}`} name={option}>
												{({ fieldProps }) => (
													<Checkbox
														{...fieldProps}
														label={option}
														name={option}
														key={`${id}-${option}`}
													/>
												)}
											</Field>
										))}
									</Fieldset>
								</Box>
								<Box xcss={textFieldStyles}>
									<Field name="feedback" label="Anything else to add?">
										{({ fieldProps }) => <TextField {...fieldProps} />}
									</Field>
								</Box>
							</Box>
							<Flex justifyContent="space-between" xcss={customFooterStyles}>
								<Checkbox
									isChecked={shouldAttachScreenshot}
									label="Attach and send a screenshot of my search results"
									onChange={handleScreenshotCheckbox}
									name="attach-screenshot"
									testId="attach-screenshot"
								/>
								<ButtonGroup label="Default button group">
									<Button
										appearance="subtle"
										onClick={() =>
											onSubmitAndClose({
												action: MODAL_ACTION.CANCELED,
												ratingReasons: [],
												shouldAttachScreenshot: false,
											})
										}
										// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
									>
										Cancel
									</Button>
									{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
									<Button appearance="primary" type="submit">
										Send feedback
									</Button>
								</ButtonGroup>
							</Flex>
						</Form>
					) : (
						<Form onSubmit={handleSubmit}>
							{({ formProps }) => (
								<form {...formProps}>
									<ModalHeader>
										<Grid gap="space.200" templateAreas={['title close']} xcss={gridStyles}>
											<Flex xcss={closeContainerStyles} justifyContent="end">
												<IconButton
													appearance="subtle"
													icon={CrossIcon}
													label="Close Modal"
													onClick={() =>
														onSubmitAndClose({
															action: MODAL_ACTION.CLOSED,
															ratingReasons: [],
															shouldAttachScreenshot: false,
														})
													}
												/>
											</Flex>
											<Flex xcss={titleContainerStyles} justifyContent="start">
												{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
												<ModalTitle>You're in the raffle! Tell us more</ModalTitle>
											</Flex>
										</Grid>
									</ModalHeader>
									<Box xcss={customBodyStyles}>
										<Flex direction="column" gap="space.200">
											{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
											<Text>
												Every time you rate your search results you’ll be in a fortnightly raffle to
												win a kudos.
											</Text>
											{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
											<Text weight="semibold">Why do you prefer these search results?</Text>
										</Flex>
										<Box xcss={fieldsetStyles}>
											<Fieldset>
												{options.map((option, id) => (
													<Field key={`${id}-${option}`} name={option}>
														{({ fieldProps }) => (
															<Checkbox
																{...fieldProps}
																label={option}
																name={option}
																key={`${id}-${option}`}
															/>
														)}
													</Field>
												))}
											</Fieldset>
										</Box>
										<Box xcss={textFieldStyles}>
											<Field name="feedback" label="Anything else to add?">
												{({ fieldProps }) => <TextField {...fieldProps} />}
											</Field>
										</Box>
									</Box>
									<Flex justifyContent="space-between" xcss={customFooterStyles}>
										<Checkbox
											isChecked={shouldAttachScreenshot}
											label="Attach and send a screenshot of my search results"
											onChange={handleScreenshotCheckbox}
											name="attach-screenshot"
											testId="attach-screenshot"
										/>
										<ButtonGroup label="Default button group">
											<Button
												appearance="subtle"
												onClick={() =>
													onSubmitAndClose({
														action: MODAL_ACTION.CANCELED,
														ratingReasons: [],
														shouldAttachScreenshot: false,
													})
												}
												// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
											>
												Cancel
											</Button>
											{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
											<Button appearance="primary" type="submit">
												Send feedback
											</Button>
										</ButtonGroup>
									</Flex>
								</form>
							)}
						</Form>
					)}
				</Modal>
			</ModalTransition>
		</div>
	);
};
